import { useState, useCallback } from 'react';
import { uploadImage } from '../api';

const ALLOWED_IMAGE_TYPES = new Set([
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  'image/svg+xml',
]);

export interface UseImageUploadOptions {
  /** Called with the markdown image snippet to insert at the cursor. */
  onInsert: (markdown: string) => void;
}

// Structural event shapes: satisfied by both React synthetic events (textarea
// composers) and native events (the CodeMirror brief editor's DOM handlers).
type PasteEventLike = {
  clipboardData: DataTransfer | null;
  preventDefault(): void;
};
type DragEventLike = {
  dataTransfer: DataTransfer | null;
  preventDefault(): void;
};

export function useImageUpload({ onInsert }: UseImageUploadOptions) {
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const handleFile = useCallback(
    async (file: File) => {
      if (!ALLOWED_IMAGE_TYPES.has(file.type)) {
        setUploadError('Only image files can be uploaded (PNG, JPG, GIF, WebP, SVG).');
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        setUploadError('Image must be smaller than 5 MiB.');
        return;
      }
      setUploadError(null);
      setUploading(true);
      try {
        const { url } = await uploadImage(file);
        const name = file.name.replace(/\.[^.]+$/, '') || 'image';
        onInsert(`![${name}](${url})`);
      } catch {
        setUploadError('Upload failed. Please try again.');
      } finally {
        setUploading(false);
      }
    },
    [onInsert],
  );

  const onPaste = useCallback(
    (e: PasteEventLike) => {
      if (!e.clipboardData) return;
      const items = Array.from(e.clipboardData.items);
      const imageItem = items.find((item) => item.kind === 'file' && ALLOWED_IMAGE_TYPES.has(item.type));
      if (!imageItem) return;
      e.preventDefault();
      const file = imageItem.getAsFile();
      if (file) void handleFile(file);
    },
    [handleFile],
  );

  const onDrop = useCallback(
    (e: DragEventLike) => {
      if (!e.dataTransfer) return;
      const files = Array.from(e.dataTransfer.files).filter((f) =>
        ALLOWED_IMAGE_TYPES.has(f.type),
      );
      if (files.length === 0) return;
      e.preventDefault();
      void handleFile(files[0]);
    },
    [handleFile],
  );

  const onDragOver = useCallback((e: DragEventLike) => {
    if (!e.dataTransfer) return;
    const hasImage = Array.from(e.dataTransfer.items).some(
      (item) => item.kind === 'file' && ALLOWED_IMAGE_TYPES.has(item.type),
    );
    if (hasImage) e.preventDefault();
  }, []);

  return { uploading, uploadError, onPaste, onDrop, onDragOver, handleFile };
}
